import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import UsageLineChart from "../../../components/charts/UsageLineChart";
import AceAvatar from "../../../components/shared/AceAvatar";
import { getDashboardData } from "../../../services/dashboardService";
import { getCurrentUser } from "../../../utils/auth";

const RANGE_OPTIONS = ["Heute", "Wöchentlich", "Monatlich", "Jährlich"];

const EMPTY_DASHBOARD = {
  stats: {
    activeUsers: 0,
    highStress: 0,
    focusMinutes: 0,
    openTasks: 0,
  },
  greeting: {
    activeUsers: 0,
    highStress: 0,
    focusMinutes: 0,
    openTasks: 0,
    pendingAccessRequests: 0,
  },
  trends: {
    activeUsers: { dir: "up", pct: 0 },
    highStress: { dir: "up", pct: 0 },
    focusMinutes: { dir: "up", pct: 0 },
    openTasks: { dir: "up", pct: 0 },
  },
  stressDistribution: {
    motiviert: 0,
    ruhig: 0,
    belastet: 0,
    averageStress: 0,
  },
  riskUsers: [],
  pendingAccessRequests: 0,
  chartDataByKpi: {
    activeUsers: [],
    highStress: [],
    focusMinutes: [],
    openTasks: [],
  },
  activities: [],
  actions: [],
};

const KPI_TITLES = {
  activeUsers: "Aktive Nutzer:innen über Zeit",
  highStress: "Auffällige Stressfälle über Zeit",
  focusMinutes: "Fokuszeit über Zeit",
  openTasks: "Offene Aufgaben über Zeit",
};

function KpiGlyph({ type }) {
  if (type === "users") {
    return (
      <svg viewBox="0 0 24 24" className="ac-dash-kpi-svg" aria-hidden="true">
        <circle cx="9" cy="9" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16.5" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M4.5 18c.9-2.7 3-4 5.5-4s4.6 1.3 5.5 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M14.5 17.5c.6-1.8 2-2.8 4-2.8 1 0 1.8.2 2.5.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "focus") {
    return (
      <svg viewBox="0 0 24 24" className="ac-dash-kpi-svg" aria-hidden="true">
        <circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M12 8.3v4.2l2.9 1.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "tasks") {
    return (
      <svg viewBox="0 0 24 24" className="ac-dash-kpi-svg" aria-hidden="true">
        <rect
          x="6.5"
          y="5.5"
          width="11"
          height="13"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M9 9.5h6M9 13h4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-dash-kpi-svg" aria-hidden="true">
      <path
        d="M12 4.5a4 4 0 0 1 4 4v2.2c0 1 .3 2 .9 2.8l1 1.4c.5.8 0 1.9-1 1.9H7.1c-1 0-1.5-1.1-1-1.9l1-1.4c.6-.8.9-1.8.9-2.8V8.5a4 4 0 0 1 4-4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M10 18.5a2.2 2.2 0 0 0 4 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PercentPill({ trend }) {
  const isUp = trend?.dir === "up";
  const percent = Math.abs(trend?.pct ?? 0);

  return (
    <div className={`ac-dash-trendpill ${isUp ? "is-green" : "is-orange"}`}>
      <span>{isUp ? "↑" : "↓"}</span>
      <span>{percent}%</span>
    </div>
  );
}

function StatCard({
  title,
  value,
  hint,
  trend,
  active,
  onClick,
  iconType,
  pulse,
}) {
  const targetValue = Number(value) || 0;
  const [displayValue, setDisplayValue] = useState(targetValue);
  const previousValueRef = useRef(targetValue);

  useEffect(() => {
    const startValue = previousValueRef.current;
    const endValue = targetValue;
    const duration = 260;
    const startTime = performance.now();

    let frameId = 0;

    const tick = (time) => {
      const progress = Math.min(1, (time - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(startValue + (endValue - startValue) * eased);

      setDisplayValue(nextValue);

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        previousValueRef.current = endValue;
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [targetValue]);

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "ac-soft-panel ac-dash-kpi",
        active ? "is-active" : "",
        pulse ? "is-pulse" : "",
      ].join(" ")}
    >
      <div className="ac-dash-kpi-top">
        <div className="ac-dash-kpi-titlewrap">
          <div className="ac-dash-kpi-icon">
            <KpiGlyph type={iconType} />
          </div>

          <div>
            <div className="ac-dash-kpi-title">{title}</div>
            <div className="ac-dash-kpi-hint">{hint}</div>
          </div>
        </div>

        <PercentPill trend={trend} />
      </div>

      <div className="ac-dash-kpi-value">
        {displayValue.toLocaleString("de-AT")}
      </div>
    </button>
  );
}

function ActionTable({ rows, onRowAction, onOpenOverview }) {
  return (
    <div className="ac-soft-panel ac-dash-block">
      <div className="ac-dash-block-head">
        <div>
          <div className="ac-dash-block-title">Handlungsübersicht</div>
          <div className="ac-dash-block-subtitle">
            Was gerade wirklich Aufmerksamkeit braucht
          </div>
        </div>

        <button
          type="button"
          onClick={onOpenOverview}
          className="ac-ghostbtn ac-dash-smallbtn ac-dash-head-action"
        >
          Benachrichtigungen öffnen
        </button>
      </div>

      <div className="ac-dash-tablewrap">
        <table className="ac-dash-table">
          <thead>
            <tr>
              <th>Bereich</th>
              <th>Beschreibung</th>
              <th>Status</th>
              <th>Aktion</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="4">
                  <div className="ac-dash-empty-inline">Kein Treffer</div>
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <span className="ac-dash-table-type">{row.type}</span>
                  </td>

                  <td className="ac-dash-table-desc">{row.desc}</td>

                  <td>
                    <span className={`ac-dash-statusbadge ${row.statusTone ?? ""}`}>
                      {row.status}
                    </span>
                  </td>

                  <td>
                    <button
                      type="button"
                      onClick={() => onRowAction?.(row)}
                      className="ac-ghostbtn ac-dash-table-btn"
                    >
                      {row.action}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityList({ items, onAll }) {
  return (
    <div className="ac-soft-panel ac-dash-block">
      <div className="ac-dash-block-head">
        <div>
          <div className="ac-dash-block-title">Letzte Aktivitäten</div>
          <div className="ac-dash-block-subtitle">
            Kürzliche Änderungen und Aktivität aus der App
          </div>
        </div>

        <AceAvatar variant="motiviert1" size={44} />
      </div>

      <div className="ac-dash-activitylist">
        {items.length === 0 ? (
          <div className="ac-dash-empty-inline">Keine Aktivitäten vorhanden</div>
        ) : (
          items.map((item, index) => (
            <div key={index} className="ac-dash-activityitem">
              <span className="ac-dash-activitydot" />
              <span>{item}</span>
            </div>
          ))
        )}
      </div>

      <div className="ac-dash-actions-row">
        <button
          type="button"
          onClick={onAll}
          className="ac-ghostbtn ac-dash-smallbtn"
        >
          Übersicht öffnen
        </button>
      </div>
    </div>
  );
}

function StressAmpel({ motiviert, ruhig, belastet, note, onGoUsers }) {
  const aceVariant =
    belastet >= 35 ? "stress2" : ruhig >= 30 ? "ruhig1" : "motiviert2";

  return (
    <div className="ac-soft-panel ac-dash-block">
      <div className="ac-dash-block-head">
        <div>
          <div className="ac-dash-block-title">Stress-Ampel</div>
          <div className="ac-dash-block-subtitle">
            Verteilung der aktuellen Belastungszustände
          </div>
        </div>

        <AceAvatar variant={aceVariant} size={48} />
      </div>

      <div className="ac-dash-stresslist">
        <div className="ac-dash-stressrow">
          <div className="ac-dash-stressleft">
            <span className="ac-dash-stressdot is-blue" />
            <span>Motiviert</span>
          </div>
          <div className="ac-dash-stressvalue">{motiviert}%</div>
        </div>

        <div className="ac-dash-stressrow">
          <div className="ac-dash-stressleft">
            <span className="ac-dash-stressdot is-green" />
            <span>Ruhig</span>
          </div>
          <div className="ac-dash-stressvalue">{ruhig}%</div>
        </div>

        <div className="ac-dash-stressrow">
          <div className="ac-dash-stressleft">
            <span className="ac-dash-stressdot is-orange" />
            <span>Belastet</span>
          </div>
          <div className="ac-dash-stressvalue">{belastet}%</div>
        </div>
      </div>

      <div className="ac-dash-stressnote">
        <span className="ac-dash-note-label">Gesamtbewertung:</span>
        <span>{note}</span>
      </div>

      <button
        type="button"
        onClick={onGoUsers}
        className="ac-primarybtn ac-dash-fullbtn"
      >
        Zu User & Klassen
      </button>
    </div>
  );
}

function ChartLegend({ currentLabel, previousLabel }) {
  return (
    <div className="ac-legend">
      <div className="ac-legend-item">
        <span className="ac-legend-line is-green" />
        <span>{currentLabel}</span>
      </div>

      <div className="ac-legend-item">
        <span className="ac-legend-line is-blue" />
        <span>{previousLabel}</span>
      </div>
    </div>
  );
}

function QuickActionCard({ title, text, onClick, iconType }) {
  return (
    <button type="button" onClick={onClick} className="ac-dash-quickcard">
      <div className="ac-dash-quickicon">
        <KpiGlyph type={iconType} />
      </div>

      <div className="ac-dash-quicktitle">{title}</div>
      <div className="ac-dash-quicktext">{text}</div>
    </button>
  );
}

// Zeitraum-Labels
function getRangeLabel(range) {
  const now = new Date();

  if (range === "Heute") {
    return now.toLocaleDateString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  if (range === "Wöchentlich") {
    const current = new Date(now);
    const day = (current.getDay() + 6) % 7;
    current.setDate(current.getDate() - day);

    const firstThursday = new Date(current.getFullYear(), 0, 4);
    const firstThursdayDay = (firstThursday.getDay() + 6) % 7;
    firstThursday.setDate(firstThursday.getDate() - firstThursdayDay + 3);

    const currentThursday = new Date(current);
    currentThursday.setDate(currentThursday.getDate() + 3);

    const week =
      1 +
      Math.round(
        (currentThursday - firstThursday) / (7 * 24 * 60 * 60 * 1000)
      );

    return `KW ${week} / ${now.getFullYear()}`;
  }

  if (range === "Monatlich") {
    const label = now.toLocaleDateString("de-AT", {
      month: "long",
      year: "numeric",
    });

    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  if (range === "Jährlich") {
    return String(now.getFullYear());
  }

  return range;
}

function getPreviousRangeLabel(range) {
  const now = new Date();

  if (range === "Heute") {
    const previous = new Date(now);
    previous.setDate(previous.getDate() - 1);

    return previous.toLocaleDateString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  if (range === "Wöchentlich") {
    const previous = new Date(now);
    previous.setDate(previous.getDate() - 7);

    const current = new Date(previous);
    const day = (current.getDay() + 6) % 7;
    current.setDate(current.getDate() - day);

    const firstThursday = new Date(current.getFullYear(), 0, 4);
    const firstThursdayDay = (firstThursday.getDay() + 6) % 7;
    firstThursday.setDate(firstThursday.getDate() - firstThursdayDay + 3);

    const currentThursday = new Date(current);
    currentThursday.setDate(currentThursday.getDate() + 3);

    const week =
      1 +
      Math.round(
        (currentThursday - firstThursday) / (7 * 24 * 60 * 60 * 1000)
      );

    return `KW ${week} / ${previous.getFullYear()}`;
  }

  if (range === "Monatlich") {
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const label = previous.toLocaleDateString("de-AT", {
      month: "long",
      year: "numeric",
    });

    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  if (range === "Jährlich") {
    return String(now.getFullYear() - 1);
  }

  return "Vergleichszeitraum";
}

function getStressNote(averageStress) {
  if (averageStress >= 4) {
    return "Mehrere Fälle sind auffällig – ein Blick auf Stress und Aufgaben ist sinnvoll.";
  }

  if (averageStress >= 3) {
    return "Die Lage ist gemischt. Einzelne Profile sollten beobachtet werden.";
  }

  return "Die allgemeine Belastung wirkt aktuell eher stabil.";
}

function getAceMessage(selectedKpi) {
  if (selectedKpi === "highStress") {
    return "Ace sagt: Kritische Belastungsfälle sollten jetzt zuerst geprüft werden.";
  }

  if (selectedKpi === "openTasks") {
    return "Ace sagt: Offene Aufgaben zeigen schnell, wo Überforderung entstehen könnte.";
  }

  if (selectedKpi === "focusMinutes") {
    return "Ace sagt: Fokuszeiten helfen dir zu sehen, ob produktives Arbeiten stabil bleibt.";
  }

  return "Ace sagt: Die aktuelle Nutzung wirkt insgesamt stabil.";
}

function getAceVariant(selectedKpi) {
  if (selectedKpi === "highStress") return "stress2";
  if (selectedKpi === "openTasks") return "motiviert1";
  if (selectedKpi === "focusMinutes") return "ruhig1";
  return "ruhig2";
}

export default function DashboardPage() {
  const navigate = useNavigate();

  // Dashboard-State
  const [range, setRange] = useState("Wöchentlich");
  const [selectedKpi, setSelectedKpi] = useState("activeUsers");
  const [pulseKpi, setPulseKpi] = useState(null);
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [loading, setLoading] = useState(true);

  const currentUser = getCurrentUser();
  const rawUserName =
    currentUser?.name ||
    currentUser?.displayName ||
    currentUser?.email?.split("@")?.[0] ||
    "Admin";

  const firstName = useMemo(() => {
    const cleaned = String(rawUserName || "").trim();
    return cleaned ? cleaned.split(/\s+/)[0] : "Admin";
  }, [rawUserName]);

  useEffect(() => {
    setSelectedKpi("activeUsers");
  }, [range]);

  useEffect(() => {
    setPulseKpi(selectedKpi);

    const timeoutId = setTimeout(() => {
      setPulseKpi(null);
    }, 260);

    return () => clearTimeout(timeoutId);
  }, [selectedKpi]);

  // Dashboard-Daten laden
  useEffect(() => {
    let isMounted = true;

    async function loadDashboard() {
      try {
        setLoading(true);

        const data = await getDashboardData(range);

        if (!isMounted) return;
        setDashboard(data);
      } catch (error) {
        console.error("Dashboard konnte nicht geladen werden:", error);

        if (!isMounted) return;
        setDashboard(EMPTY_DASHBOARD);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [range]);

  // Navigation mit Dashboard-Kontext
  const goWithDashboardState = (path) => {
    navigate(path, {
      state: {
        fromDashboard: true,
        dashboardRange: range,
        dashboardKpi: selectedKpi,
      },
    });
  };

  const { greeting, trends, stressDistribution, activities, actions } = dashboard;
  const riskUsers = dashboard.riskUsers || [];

  const kpis = useMemo(
    () => [
      {
        id: "activeUsers",
        title: "Aktive Nutzer:innen",
        value: String(dashboard.stats.activeUsers),
        hint: "In diesem Zeitraum aktiv",
        trend: trends.activeUsers,
        iconType: "users",
        unitLabel: "Nutzer:innen",
      },
      {
        id: "highStress",
        title: "Auffällige Stressfälle",
        value: String(dashboard.stats.highStress),
        hint: "Sofort prüfen",
        trend: trends.highStress,
        iconType: "stress",
        unitLabel: "Fälle",
      },
      {
        id: "focusMinutes",
        title: "Fokusminuten",
        value: String(dashboard.stats.focusMinutes),
        hint: "Gesamte Fokuszeit",
        trend: trends.focusMinutes,
        iconType: "focus",
        unitLabel: "Minuten",
      },
      {
        id: "openTasks",
        title: "Offene Aufgaben",
        value: String(dashboard.stats.openTasks),
        hint: "Noch nicht erledigt",
        trend: trends.openTasks,
        iconType: "tasks",
        unitLabel: "Aufgaben",
      },
    ],
    [dashboard.stats, trends]
  );

  const selectedKpiMeta = useMemo(
    () => kpis.find((item) => item.id === selectedKpi) ?? kpis[0],
    [kpis, selectedKpi]
  );

  const chartTitle = useMemo(
    () => KPI_TITLES[selectedKpi] ?? "Entwicklung über Zeit",
    [selectedKpi]
  );

  const chartData = useMemo(
    () => dashboard.chartDataByKpi?.[selectedKpi] ?? [],
    [dashboard.chartDataByKpi, selectedKpi]
  );

  const kpiAnimKey = `${range}-${dashboard.stats.activeUsers}-${dashboard.stats.highStress}-${dashboard.stats.focusMinutes}-${dashboard.stats.openTasks}`;
  const chartAnimKey = `${range}-${selectedKpi}`;
  const periodLabel = useMemo(() => getRangeLabel(range), [range]);
  const previousPeriodLabel = useMemo(() => getPreviousRangeLabel(range), [range]);

  return (
    <DashboardLayout title="Dashboard">
      <div className="ac-page-enter">
        {/* Hero */}
        <section className="ac-soft-panel ac-dash-hero">
          <div className="ac-dash-hero-grid">
            <div>
              <h1 className="ac-dash-hero-title">
                {loading ? "Dashboard wird geladen..." : `Hallo, ${firstName}!`}
              </h1>

              <p className="ac-dash-hero-text">
                Aktuell sind <strong>{greeting.activeUsers}</strong> Nutzer:innen aktiv,
                <strong> {greeting.highStress}</strong> Fälle brauchen gerade besondere Aufmerksamkeit,
                <strong> {greeting.focusMinutes.toLocaleString("de-AT")}</strong> Fokusminuten wurden erfasst
                und <strong> {greeting.openTasks}</strong> Aufgaben sind noch offen.
                {greeting.pendingAccessRequests > 0 && (
                  <>
                    {" "}Außerdem warten <strong>{greeting.pendingAccessRequests}</strong> Zugangsanfrage(n) auf Prüfung.
                  </>
                )}
              </p>
            </div>

            <div className="ac-dash-hero-side">
              <div className="ac-dash-acebox">
                <AceAvatar variant={getAceVariant(selectedKpi)} size={56} />
                <div className="ac-dash-acebubble">{getAceMessage(selectedKpi)}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Stress Intelligence */}
        {riskUsers.length > 0 && (
          <section className="ac-soft-panel ac-dash-block" style={{ marginTop: 18 }}>
            <div className="ac-dash-block-head">
              <div>
                <div className="ac-dash-block-title">Stress Intelligence</div>
                <div className="ac-dash-block-subtitle">
                  Diese Profile wirken aktuell besonders belastet
                </div>
              </div>

              <button
                type="button"
                onClick={() => goWithDashboardState("/users-classes/users")}
                className="ac-primarybtn ac-dash-smallbtn"
              >
                Jetzt prüfen
              </button>
            </div>

            <div className="ac-dash-activitylist" style={{ marginTop: 14 }}>
              {riskUsers.slice(0, 4).map((item) => (
                <div key={item.uid} className="ac-dash-activityitem">
                  <span className="ac-dash-activitydot" />
                  <span>
                    <strong>{item.name}</strong>
                    {item.className ? ` · ${item.className}` : ""}
                    {" — "}
                    {item.highStressCount} hohe Stresswerte, {item.openTasks} offene Aufgaben
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* KPIs + Chart */}
        <section className="ac-soft-panel ac-dash-block" style={{ marginTop: 18 }}>
          <div className="ac-dash-block-head">
            <div>
              <div className="ac-dash-block-title">Übersicht</div>
              <div className="ac-dash-block-subtitle">
                Wähle einen Zeitraum und prüfe die wichtigsten Kennzahlen
              </div>
              <div className="ac-dash-block-subtitle" style={{ marginTop: 4 }}>
                Zeitraum: {periodLabel}
              </div>
            </div>

            <select
              value={range}
              onChange={(event) => setRange(event.target.value)}
              className="ac-select ac-dash-range-select"
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>

          <div key={kpiAnimKey} className="ac-dash-kpi-grid">
            {kpis.map((kpi) => (
              <StatCard
                key={kpi.id}
                title={kpi.title}
                value={kpi.value}
                hint={kpi.hint}
                trend={kpi.trend}
                iconType={kpi.iconType}
                active={selectedKpi === kpi.id}
                pulse={pulseKpi === kpi.id}
                onClick={() => setSelectedKpi(kpi.id)}
              />
            ))}
          </div>

          <div key={chartAnimKey} className="ac-dash-chartarea">
            <div className="ac-dash-charthead">
              <div>
                <div className="ac-dash-block-title">{chartTitle}</div>
                <div className="ac-dash-block-subtitle">
                  Einheit: {selectedKpiMeta.unitLabel}
                </div>
              </div>
            </div>

            <div className="ac-dash-chartlegend">
              <ChartLegend
                currentLabel={periodLabel}
                previousLabel={previousPeriodLabel}
              />
            </div>

            <UsageLineChart
              data={chartData}
              unitLabel={selectedKpiMeta.unitLabel}
              currentLabel={periodLabel}
              previousLabel={previousPeriodLabel}
            />
          </div>
        </section>

        {/* Main grid */}
        <section className="ac-dash-grid-main" style={{ marginTop: 18 }}>
          <div className="ac-dash-span-2">
            <div className="ac-soft-panel ac-dash-block">
              <div className="ac-dash-block-head">
                <div>
                  <div className="ac-dash-block-title">Quick Actions</div>
                  <div className="ac-dash-block-subtitle">
                    Schnellzugriffe für sinnvolle Admin-Schritte
                  </div>
                </div>

                <AceAvatar variant="ruhig1" size={42} />
              </div>

              <div className="ac-dash-quickgrid">
                <QuickActionCard
                  title="Stressfälle prüfen"
                  text="Auffällige Profile direkt öffnen"
                  iconType="stress"
                  onClick={() => goWithDashboardState("/users-classes/users")}
                />

                <QuickActionCard
                  title="Klassen prüfen"
                  text="Belastete Klassen schneller erkennen"
                  iconType="users"
                  onClick={() => goWithDashboardState("/users-classes/classes")}
                />

                <QuickActionCard
                  title="Feedback beantworten"
                  text="Offene Rückmeldungen bearbeiten"
                  iconType="tasks"
                  onClick={() => goWithDashboardState("/feedback")}
                />

                <QuickActionCard
                  title="Statistiken öffnen"
                  text="Stress, Fokus und Aktivität prüfen"
                  iconType="focus"
                  onClick={() => goWithDashboardState("/stats")}
                />

                <QuickActionCard
                  title="Inhalte verwalten"
                  text="Tipps und Challenges prüfen"
                  iconType="tasks"
                  onClick={() => goWithDashboardState("/content")}
                />

                <QuickActionCard
                  title="Zugangsanfragen"
                  text="Neue Anfragen prüfen und freigeben"
                  iconType="users"
                  onClick={() => goWithDashboardState("/access-requests")}
                />
              </div>
            </div>
          </div>

          <div>
            <ActivityList
              items={activities}
              onAll={() => goWithDashboardState("/notifications")}
            />
          </div>

          <div className="ac-dash-span-2">
            <ActionTable
              rows={actions}
              onOpenOverview={() => goWithDashboardState("/notifications")}
              onRowAction={(row) => row.go && goWithDashboardState(row.go)}
            />
          </div>

          <div>
            <StressAmpel
              motiviert={stressDistribution.motiviert}
              ruhig={stressDistribution.ruhig}
              belastet={stressDistribution.belastet}
              note={getStressNote(stressDistribution.averageStress)}
              onGoUsers={() => goWithDashboardState("/users-classes")}
            />
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}